/**
 * CalendarRoute — /kalender (Plan 03-06, extended Plan 03-07).
 *
 * #157 (redesign steg 7a, design/screens/Stell_Kalender.dc.html, D8):
 *   1. datopanelet: «Plan» med ny hendelse (`add-event-cta`) og avataren,
 *      Plan-fanene (Kalender · Middag · Handle), Måned / Uke / Dag, perioden
 *      med I dag og forrige/neste, og i Uke ukestripen med prikker per person;
 *   2. under panelet: månedsrutenettet, dagsagendaen (Uke) eller tidslinjen
 *      (Dag).
 * Kalenderen åpner på Uke (D8). Skallet legger ikke sin egen topp over
 * Kalender (`drawsOwnHeader`), og Plan-fanene står i panelet (`ownTabs`).
 *
 * Forrige/neste flytter en måned, en uke eller en dag. I Uke er den valgte
 * dagen ankeret, så en uke fram beholder ukedagen.
 *
 * D-16: calendar mutations are online-only. This shell mounts without auth
 * checking — the QueryClient will return 401 errors which TanStack Query
 * surfaces to callers.
 *
 * T-CAL-31: fetch uses credentials:'include' (in useCalendarRange).
 */
import { useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { addDays, addMonths, isSameDay, isValid, parseISO } from 'date-fns';
import { DatePanel, ErrorState, Icon, SegmentedTabs } from '../../components/primitives';
import { PANEL_ROUND } from '../../components/primitives/DatePanel';
import { MeAvatarLink } from '../../components/ScreenHeader';
import { FeedbackWidget } from '../feedback/FeedbackWidget';
import { SubTabs } from '../dashboard/SubTabsLayout';
import { useToday } from '../dashboard/tiles/DateTile';
import { useCalendarRange, type CalendarView, type EventInstance } from './useCalendarRange';
import { MonthView } from './MonthView';
import { WeekDayStrip, WeekView } from './WeekView';
import { DayView } from './DayView';
import { EventEditSheet, type EventEditSheetMode } from './EventEditSheet';
import { useAttendeeLookup, type AttendeeLookup } from './attendees';
import { useSession, type SessionMember } from '../auth/useSession';
import { capitalizeFirst, formatWeekNumber } from '../../i18n/formatters';
import { fromRRuleString } from '@stell/shared';

/** Måned / Uke / Dag. Testid-ene står bokstavelig, så testid-kontrakten ser dem. */
const VIEW_OPTIONS = [
  { value: 'month', i18nKey: 'calendar.view.month', testId: 'view-month' },
  { value: 'week', i18nKey: 'calendar.view.week', testId: 'view-week' },
  { value: 'day', i18nKey: 'calendar.view.day', testId: 'view-day' },
] as const satisfies readonly { value: CalendarView; i18nKey: string; testId: string }[];

/**
 * `?date=YYYY-MM-DD` fra påminnelsen er en dato i Oslo (event-reminders.service),
 * så den leses som lokal midnatt, ikke UTC (`new Date('2026-05-12')`), som ville
 * gitt dagen før vest for UTC. Ugyldig eller manglende dato gir null.
 */
function parseLinkDate(raw: string | null): Date | null {
  if (!raw) return null;
  const d = parseISO(raw);
  return isValid(d) ? d : null;
}

/** Arket er enten «ny hendelse» eller den trykkede hendelsen. */
type SheetState = Extract<EventEditSheetMode, { kind: 'create' }> | { kind: 'edit'; ev: EventInstance };

/**
 * Redigeringsarket for en hendelse, uansett hvilken visning den ble trykket i.
 *
 * #165: gjentakelsen og skaperen kommer fra forekomsten, seeren fra økta. En
 * forekomst av en serie er `isOccurrence` (også når den ikke er overstyrt), så
 * omfangsvelgeren vises. Til økta er lastet er rollen `child`: Slett vises ikke
 * før vi vet hvem som ser (D-08, T-CAL-37 — API-et sjekker uansett).
 */
function editMode(
  ev: EventInstance,
  viewer: SessionMember | undefined,
  attendees: AttendeeLookup,
): EventEditSheetMode {
  const recurrence = ev.recurrence_rule ? fromRRuleString(ev.recurrence_rule) : null;
  const createdBy = ev.created_by ?? '';
  const createdByName = attendees.get(createdBy)?.name;
  return {
    kind: 'edit',
    eventId: ev.id,
    seriesId: ev.series_id,
    isOccurrence: !!ev.recurrence_rule,
    occurrenceStart: ev.occurrence_start,
    initial: {
      title: ev.title,
      description: ev.description,
      starts_at: ev.starts_at,
      ends_at: ev.ends_at,
      all_day: ev.all_day,
      location: ev.location,
      color_token: ev.color_token,
      ...(recurrence ? { recurrence } : {}),
      attendee_ids: ev.attendee_ids,
    },
    createdBy,
    ...(createdByName ? { createdByName } : {}),
    viewerId: viewer?.id ?? '',
    viewerRole: viewer?.role ?? 'child',
  };
}

export function CalendarRoute(): ReactNode {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const deepLinkId = searchParams.get('id');
  const deepLinkDate = searchParams.get('date');
  const linkDate = parseLinkDate(deepLinkDate);
  const today = useToday();

  // D8: kalenderen åpner på Uke. En push-lenke med ?date= åpner Dag på den
  // datoen allerede ved første tegning, så Uke ikke blinker og henter først.
  const [view, setView] = useState<CalendarView>(() => (linkDate ? 'day' : 'week'));
  const [anchor, setAnchor] = useState<Date>(() => linkDate ?? new Date());
  // Plan 06-02 Task 2: surface isError + refetch at the route level so a
  // /api/calendar/events failure renders <ErrorState variant="page">
  // instead of a silently-empty calendar. The queryFn-internal `!r.ok →
  // throw` (useCalendarRange.ts) feeds isError here.
  const {
    data: events = [],
    isError,
    refetch,
  } = useCalendarRange(view, anchor);
  const attendees = useAttendeeLookup();
  const { data: session } = useSession();
  const [sheetState, setSheetState] = useState<SheetState | null>(null);

  // D-10 push-lenke: en ny ?id=...&date=... mens ruten står åpen, går til Dag
  // på den lenkede datoen. #157: avhenger også av id, så en ny påminnelse for
  // samme dato går tilbake til Dag.
  // T-CAL-41: deepLinkId is only used for class toggle in DayView — never innerHTML.
  const linkKey = linkDate ? linkDate.getTime() : null;
  useEffect(() => {
    if (linkKey === null) return;
    setView('day');
    setAnchor(new Date(linkKey));
  }, [linkKey, deepLinkId]);

  // Uthevingen gjelder bare den lenkede dagen: seriens id peker ut én
  // forekomst, ikke alle tirsdagene man blar til etterpå (#157).
  const highlightEventId =
    deepLinkId && linkDate && isSameDay(anchor, linkDate) ? deepLinkId : undefined;

  /**
   * Open the create sheet pre-filled with the clicked day. Views hand us a
   * Date at local midnight; we keep the date but default the time to the
   * current time rounded up to the next 15-min boundary (mirrors the bare
   * "Legg til hendelse" default, just date-shifted to the tapped day).
   */
  const openCreateOnDate = (date: Date) => {
    const now = new Date();
    const start = new Date(date);
    start.setHours(now.getHours(), Math.ceil(now.getMinutes() / 15) * 15, 0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    const startsAt = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}`;
    setSheetState({ kind: 'create', defaults: { starts_at: startsAt } });
  };

  const openEdit = (ev: EventInstance) => setSheetState({ kind: 'edit', ev });

  /** Shift anchor date forward/back by one view-unit. */
  const shiftBy = (n: number) => {
    if (view === 'month') {
      setAnchor((a) => addMonths(a, n));
    } else if (view === 'week') {
      setAnchor((a) => addDays(a, n * 7));
    } else {
      setAnchor((a) => addDays(a, n));
    }
  };

  /** Perioden i panelet: «Oktober 2026» i Måned, ellers «Oktober · uke 43». */
  const month = anchor.toLocaleDateString('nb-NO', { month: 'long' });
  const periodLabel =
    view === 'month'
      ? capitalizeFirst(anchor.toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' }))
      : t('calendar.panel.week', { month: capitalizeFirst(month), week: formatWeekNumber(anchor) });

  const panel = (
    <DatePanel
      title={t('nav.plan')}
      className="md:mx-8 md:mt-4 md:rounded-2xl"
      action={
        <div className="flex items-center gap-2">
          {/* Panelet har alltid mørk tekst, så knappen er fylt on-panel i
              begge temaer — ikke Button sin solid, som snur i mørkt tema. */}
          <button
            type="button"
            data-testid="add-event-cta"
            onClick={() => setSheetState({ kind: 'create' })}
            aria-label={t('calendar.action.addEvent')}
            className={`${PANEL_ROUND} bg-on-panel text-on-panel-solid`}
          >
            <Icon.Plus size={22} />
          </button>
          {/* #187: tilbakemelding fra skjermen, på panelets flate. */}
          <FeedbackWidget variant="icon" tone="panel" />
          <MeAvatarLink />
        </div>
      }
    >
      <SubTabs group="plan" tone="panel" className="mt-3" />

      <SegmentedTabs
        tone="panel"
        label={t('calendar.view.label')}
        data-testid="view-segmented"
        className="mt-3 md:max-w-xl"
        value={view}
        onChange={setView}
        options={VIEW_OPTIONS.map((o) => ({ value: o.value, label: t(o.i18nKey), testId: o.testId }))}
      />

      <div className="mt-3 flex items-center gap-1">
        <h2
          data-testid="calendar-header"
          aria-live="polite"
          className="m-0 min-w-0 flex-1 truncate text-sm font-extrabold"
        >
          {periodLabel}
        </h2>
        <button
          type="button"
          onClick={() => setAnchor(new Date())}
          data-testid="nav-today"
          className="flex h-11 shrink-0 items-center rounded-pill px-3.5 text-sm font-extrabold hover:bg-on-panel/10"
        >
          {t('calendar.action.today')}
        </button>
        <button
          type="button"
          onClick={() => shiftBy(-1)}
          aria-label={t('calendar.action.prev')}
          data-testid="nav-prev"
          className={`${PANEL_ROUND} hover:bg-on-panel/10`}
        >
          <Icon.ChevL size={20} />
        </button>
        <button
          type="button"
          onClick={() => shiftBy(1)}
          aria-label={t('calendar.action.next')}
          data-testid="nav-next"
          className={`${PANEL_ROUND} hover:bg-on-panel/10`}
        >
          <Icon.ChevR size={20} />
        </button>
      </div>

      {view === 'week' && !isError && (
        <WeekDayStrip
          anchor={anchor}
          today={today}
          events={events}
          attendees={attendees}
          onSelect={setAnchor}
        />
      )}
    </DatePanel>
  );

  return (
    <main
      aria-label={t('nav.calendar')}
      className="flex min-h-dvh flex-col gap-4 pb-6"
    >
      {panel}

      <div className="px-5 md:px-8">
        {/* Plan 06-02 Task 2 (D-02): error fallback when the calendar range
            query fails (network, 500, etc.). #157: panelet står over feilen,
            så Plan-fanene og avataren ikke forsvinner, og feilen er inline
            (panelet har allerede sidens h1). */}
        {isError ? (
          <div data-testid="calendar-error">
            <ErrorState
              variant="inline"
              heading={t('calendar.error.load.heading')}
              body={t('calendar.error.load.body')}
              onRetry={() => {
                void refetch();
              }}
            />
          </div>
        ) : view === 'month' ? (
          <MonthView
            anchor={anchor}
            events={events}
            onDayClick={openCreateOnDate}
            onEventClick={openEdit}
          />
        ) : view === 'week' ? (
          <WeekView
            anchor={anchor}
            events={events}
            attendees={attendees}
            onEmptySlotClick={openCreateOnDate}
            onEventClick={openEdit}
          />
        ) : (
          <DayView
            anchor={anchor}
            events={events}
            attendees={attendees}
            {...(highlightEventId ? { highlightEventId } : {})}
            onEmptySlotClick={openCreateOnDate}
            onEventClick={openEdit}
          />
        )}
      </div>

      {/* EventEditSheet — create / edit. Redigeringsmodusen bygges ved tegning,
          så seeren og skaperens navn kommer med også når de lastes etter trykket. */}
      {sheetState && (
        <EventEditSheet
          open={!!sheetState}
          onClose={() => setSheetState(null)}
          mode={
            sheetState.kind === 'edit'
              ? editMode(sheetState.ev, session?.member, attendees)
              : sheetState
          }
        />
      )}
    </main>
  );
}
